<script setup>
import { gsap, ScrollTrigger } from '/vendors/gsap'
import GlobalEmitter from '/glxp/utils/emitter'
import { useIsMobile } from '~~/composables/useIsMobile'
import { useFullHeight } from '~~/composables/useFullHeight'
import { onLoadAnimationEnd } from '~~/composables/onLoadAnimationEnd'
import { sectionPins } from '/utils/sectionPins'

const PARALLAX_AMOUNT = 100
const MOBILE_SCROLL_MULTIPLIER = 0.5

const props = defineProps({
  slug: {
    type: String,
    default: null,
  },
  backgroundImage: {
    type: String,
    default: null,
  },
  isLast: {
    type: Boolean,
    default: false,
  },
})
const emit = defineEmits(['bgUpdate'])

const pinProps = computed(() => sectionPins[props.slug] || {})

const isMobile = useIsMobile()
const fullHeight = useFullHeight()

const startBufferHeight = computed(() => {
  if (pinProps.value?.bufferStart) {
    return (
      pinProps.value.bufferStart *
      (isMobile.value ? MOBILE_SCROLL_MULTIPLIER : 1)
    )
  }
  return 0
})
const endBufferHeight = computed(() => {
  if (pinProps.value?.bufferEnd) {
    return (
      pinProps.value.bufferEnd * (isMobile.value ? MOBILE_SCROLL_MULTIPLIER : 1)
    )
  }
  return 0
})

const container = ref(null)
const content = ref(null)
const backgroundImageEl = ref(null)

const route = useRoute()
const isHomepage = computed(() => route.name.startsWith('index'))

let st

const isFade = computed(() => pinProps.value?.clipStyle === 'sticky-fade')
const isClip = computed(
  () =>
    ['reveal', 'double-clip', 'reveal-no-bottom'].indexOf(
      pinProps.value?.clipStyle
    ) !== -1
)

/**
 * Reveal
 */
const isRevealed = ref(false)
function emitReveal() {
  GlobalEmitter.emit('reveal', props.slug)
  GlobalEmitter.emit(`reveal:${props.slug}`)
  if (isFade.value) {
    container.value.classList.remove('clipped-section--hidden')
  }
}

function emitUnreveal() {
  GlobalEmitter.emit('unreveal', props.slug)
  GlobalEmitter.emit(`unreveal:${props.slug}`)
  if (isFade.value) {
    container.value.classList.add('clipped-section--hidden')
  }
}

watch(isRevealed, (value) => {
  if (value) {
    emitReveal()
  } else {
    emitUnreveal()
  }
})

/**
 * Pinning
 */
const isPinnedTop = ref(false)
const isPinnedBottom = ref(false)
const wrapper = ref(null)

let wrapperHeight
function setWrapperHeight() {
  if (!content.value) return
  const { height } = content.value.getBoundingClientRect()
  const roundedHeight = Math.round(height)
  wrapper.value.style.height = `${roundedHeight}px`
  wrapperHeight = roundedHeight
}

function clearWrapperHeight() {
  wrapper.value.style.height = undefined
}

onLoadAnimationEnd(() => {
  setWrapperHeight()
  GlobalEmitter.on('debouncedResize', setWrapperHeight)
})

onBeforeUnmount(() => {
  clearWrapperHeight()
  GlobalEmitter.off('debouncedResize', setWrapperHeight)
})

watch(isPinnedTop, (value) => {
  if (value) {
    GlobalEmitter.emit(`pin:${props.slug}`)
  } else {
    GlobalEmitter.emit(`unpin:${props.slug}`)
  }
})

watch(isPinnedBottom, (value) => {
  if (value) {
    GlobalEmitter.emit(`pin:${props.slug}`)
  } else {
    GlobalEmitter.emit(`unpin:${props.slug}`)
  }
})

/**
 * Timeline
 */
onLoadAnimationEnd(() => {
  const clipAngle = isMobile.value ? '60px' : '200px'
  const enterTween =
    isClip.value &&
    gsap
      .timeline({
        paused: true,
        // clearProps: true,
      })
      .fromTo(
        content.value,
        {
          clipPath: `polygon(
            0% calc(100vh + ${clipAngle}),
            100% calc(100vh - 0px),
            100% 100%,
            0% 100%
          )`,
        },
        {
          clipPath: `polygon(
            0% calc(0vh + 0px),
            100% calc(0vh - ${clipAngle}),
            100% 100%,
            0% 100%
          )`,
          duration: 1,
          ease: 'linear',
        }
      )

  if (pinProps.value.preroll) {
    enterTween.from(
      content.value.querySelector('.clipped-section__content-inner'),
      {
        y: `${pinProps.value.preroll * 100}vh`,
        duration: 1,
        ease: 'power2.in',
        force3D: true,
      },
      0
    )
  }

  const exitTween = isClip.value && gsap.timeline({ paused: true })
  if (!pinProps.value.noBottomCrop && isClip.value) {
    exitTween.fromTo(
      content.value,
      {
        clipPath: `polygon(
            0% 0%,
            100% 0%,
            100% calc(100% - 0vh - 0px),
            0% calc(100% - 0vh + ${clipAngle})
          )`,
      },
      {
        clipPath: `polygon(
            0% 0%,
            100% 0%,
            100% calc(100% - 100vh - ${clipAngle}),
            0% calc(100% - 100vh + 0px)
          )`,
        duration: 1,
        ease: 'linear',
      },
      0
    )
  }
  if (pinProps.value.postroll) {
    exitTween.to(
      content.value.querySelector('.clipped-section__content-inner'),
      {
        y: `-=${pinProps.value.postroll * 100}vh`,
        duration: 1,
        ease: 'power2.out',
        force3D: true,
      },
      0
    )
  }

  // if (enterTween) enterTween.progress(1).progress(0)
  // if (exitTween) exitTween.progress(1).progress(0)

  function onUpdate({ progress, start, end, isActive, direction }) {
    const windowHeight = window.innerHeight
    const clientHeight = document.documentElement.clientHeight
    const chromeHeight = fullHeight.value - clientHeight
    const sectionHeight = end - start
    const pixelProgress = sectionHeight * progress

    /**
     * Pinning
     */
    const pinTopEnd = windowHeight + startBufferHeight.value * windowHeight
    const pinBottomStart =
      sectionHeight -
      (windowHeight + endBufferHeight.value * windowHeight) -
      chromeHeight

    isPinnedTop.value = !!isActive && pixelProgress <= pinTopEnd
    isPinnedBottom.value =
      !!isActive &&
      !pinProps.value.noBottomPin &&
      pixelProgress >= pinBottomStart

    /**
     * Swipes
     */
    const swipeProgress = Math.min(pixelProgress / windowHeight, 1)
    const endSwipeProgress = Math.max(
      (pixelProgress - sectionHeight + windowHeight) / windowHeight,
      0
    )

    if (isClip.value) {
      if (progress < 0.5) {
        enterTween.seek(swipeProgress)
      } else if (progress >= 0.5) {
        exitTween.seek(endSwipeProgress)
      }
    }

    /**
     * Parallax and scale for background images
     */
    const SCALE_MIN = 1
    const SCALE_MAX = 1.3

    const SCALE_DURATION = 1.3
    const scaleProgress = Math.min(
      pixelProgress / (windowHeight * SCALE_DURATION),
      1
    )
    const endScaleProgress = Math.max(
      (pixelProgress - sectionHeight + windowHeight * SCALE_DURATION) /
        (windowHeight * SCALE_DURATION),
      0
    )
    const easeOut = gsap.parseEase('power1.out')
    const easeIn = gsap.parseEase('power1.in')
    const scaleProgressEased = easeOut(scaleProgress)
    const endScaleProgressEased = easeIn(endScaleProgress)
    const scale =
      SCALE_MIN +
      (1 - scaleProgressEased) * (SCALE_MAX - SCALE_MIN) +
      endScaleProgressEased * (SCALE_MAX - SCALE_MIN)
    const translation = (1 - progress - 0.5) * PARALLAX_AMOUNT

    emit('bgUpdate', { scale, translation })

    if (props.backgroundImage && backgroundImageEl.value) {
      backgroundImageEl.value.style.transform = `translate3d(0, ${translation}px, 0) scale(${scale})`
    }

    /**
     * Internal trigger timing
     */
    const updateProps = {
      progress,
      start,
      end,
      direction,
      isActive,
      windowHeight,
      clientHeight,
      chromeHeight,
      sectionHeight,
      pixelProgress,
    }
    const { reveal: revealFunction } = pinProps.value
    if (isClip.value) {
      const defaultRevealFunction = ({
        pixelProgress,
        windowHeight,
        sectionHeight,
        direction,
      }) => {
        return (
          (direction >= 0
            ? pixelProgress > windowHeight * 0.5
            : pixelProgress >= 100) && pixelProgress < sectionHeight
        )
      }

      if (
        revealFunction
          ? revealFunction(updateProps)
          : defaultRevealFunction(updateProps)
      ) {
        isRevealed.value = true
      } else {
        isRevealed.value = false
      }
    } else if (isFade.value) {
      if (
        revealFunction
          ? revealFunction(updateProps)
          : pixelProgress > windowHeight / 2 &&
            pixelProgress < sectionHeight - windowHeight
      ) {
        isRevealed.value = true
      } else {
        isRevealed.value = false
      }
    }
  }

  st = ScrollTrigger.create({
    trigger: container.value,
    start: 'top bottom',
    end: 'bottom top',
    onToggle({ isActive }) {
      if (!isActive) {
        isPinnedTop.value = false
        isPinnedBottom.value = false
      }
    },
    onUpdate,
    onRefresh: onUpdate,
  })

  // Set initial fade value class
  if (isFade.value) {
    container.value.classList.add('clipped-section--hidden')
  }
})

onBeforeUnmount(() => {
  st?.kill()
})
</script>

<template>
  <div
    ref="container"
    class="clipped-section"
    :class="[
      `clipped-section--slug-${slug}`,
      {
        [`clipped-section--${pinProps.clipStyle}`]: !!pinProps?.clipStyle,
        'clipped-section--homepage': isHomepage,
        'clipped-section--has-bg': !!backgroundImage,
      },
    ]"
  >
    <div
      class="clipped-section__pin-spacer clipped-section__pin-spacer--start"
      :style="{
        height: `calc(${startBufferHeight} * var(--real-height, 100vh))`,
      }"
    />
    <div class="clipped-section__content-wrapper" ref="wrapper">
      <div
        ref="content"
        class="clipped-section__content"
        :class="{
          'pinned--top': isPinnedTop,
          'pinned--bottom': isPinnedBottom,
        }"
      >
        <div class="clipped-section__content-inner">
          <slot />

          <div
            v-if="backgroundImage"
            ref="backgroundImageEl"
            class="clipped-section__background"
          >
            <img :src="backgroundImage" alt=""/>
          </div>
        </div>
      </div>
    </div>
    <div
      class="clipped-section__pin-spacer clipped-section__pin-spacer--end"
      :style="{
        height: `calc(${endBufferHeight} * var(--real-height, 100vh))`,
      }"
    />
  </div>
</template>

<style lang="scss">
// Vendors
@import 'styles/vendors/sass-mq/mq';

// Utils
@import 'styles/utils/functions';

.clipped-section {
  position: relative;
  z-index: 1;
  transition: opacity 0.5s, visibility 0.5s;
  overflow: hidden;

  &--slug-zero-gravity,
  &--slug-preflight-training-program {
    // any section that uses position: sticky can't have overflow: hidden on a parent component
    overflow: visible;
  }

  &__content {
    position: relative;

    &-inner {
      backface-visibility: hidden;
      will-change: transform;
    }

    &.pinned--top,
    &.pinned--bottom {
      position: fixed;
      left: 0;
      width: 100%;
    }

    &.pinned--top {
      top: 0;
    }

    &.pinned--bottom {
      bottom: 0;
    }
  }

  &__background {
    position: absolute;
    top: -50px;
    left: 0;
    z-index: 0;
    width: 100%;
    height: calc(100% + 100px);

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  &--reveal {
    z-index: 2;
  }

  &--sticky-fade {
    transition: opacity 0.5s, visibility 0.5s;
    margin-top: 0;
    .home-section__wrapper,
    .hotspot-section__inner {
      width: 100%;
      height: var(--real-height, 100vh);
    }
  }

  &--hidden {
    opacity: 0;
    visibility: hidden;
    // transition-delay: 1.5s;
  }

  &--none {
    margin-top: 0;
  }
}
</style>
